import {
  Modal,
  Box,
  Typography,
  Button,
  Grid,
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  TextField,
  Stack,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { Select as MuiSelect } from "@mui/material";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { DateTimePickerComponent } from "../../../Playlists/components/DateTimePicker";
import { UserModalContainer } from "styles/flex";
import { ModalStyle } from "../AddTreatmentModal/styles";
import { UPDATE_TREATMENT } from "./queries";
import { GET_TREATMENT_HISTORY } from "../TreatmentHistoryTable/queries";
import {
  EditTreatmentProps,
  EditTreatmentFormInputs,
  UpdateTreatmentVariables,
  UpdateTreatmentResponse,
} from "./types";
import { Text, Title } from "./styles";
import { useSpecialistDropdown } from "hooks/useSpecialistDropDown";

export const EditTreatmentModal = ({
  open,
  onClose,
  treatmentData,
  onSuccess,
  account,
}: EditTreatmentProps) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [successMessage, setSuccessMessage] = useState<string>();
  const [startDateValue, setStartDateValue] = useState<Date | null>(null);
  const [endDateValue, setEndDateValue] = useState<Date | null>(null);

  const { options: specialistData } = useSpecialistDropdown();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<EditTreatmentFormInputs>({
    mode: "onChange",
    defaultValues: {
      programType: "",
      roundNumber: 1,
      start_date: null,
      end_date: null,
    },
  });

  const watchStart = watch("start_date");

  const { data: historyData, refetch } = useQuery(GET_TREATMENT_HISTORY, {
    variables: { accountId: account },
    fetchPolicy: "network-only",
    skip: !account,
  });

  const [updateTreatment, { loading }] = useMutation<
    UpdateTreatmentResponse,
    UpdateTreatmentVariables
  >(UPDATE_TREATMENT, {
    fetchPolicy: "no-cache",
    onError: (err) => setErrorMessage(err.message),
  });

  useEffect(() => {
    if (open) refetch();
  }, [open, refetch]);

  useEffect(() => {
    if (treatmentData && open) {
      const s = treatmentData.startDate
        ? new Date(treatmentData.startDate)
        : null;
      const e = treatmentData.endDate ? new Date(treatmentData.endDate) : null;

      reset(); // ensure fresh form state
      setValue("programType", treatmentData.programType);
      setValue("roundNumber", treatmentData.roundNumber);
      setValue("start_date", s);
      setValue("end_date", e);
      setValue("specialist_id", treatmentData.specialist.id);

      setStartDateValue(s);
      setEndDateValue(e);
    }
  }, [treatmentData, open, setValue, reset]);

  const onSubmit: SubmitHandler<EditTreatmentFormInputs> = async (formData) => {
    if (!treatmentData?.id) {
      setErrorMessage("Missing treatment ID.");
      return;
    }

    const newStart = new Date(formData.start_date!);
    const newEnd = formData.end_date ? new Date(formData.end_date) : null;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const currentProgramType = treatmentData.programType;

    // Get all treatments except the current one being edited
    const otherTreatments =
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      historyData?.treatmentHistory?.filter((t: any) => {
        return t.id !== treatmentData.id;
      }) || [];

    // Check for overlaps with any treatments (regardless of program type)
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const overlappingProgram = otherTreatments.find((t: any) => {
      const existingStart = new Date(t.start_date);
      const existingEnd = t.end_date ? new Date(t.end_date) : null;

      // Check for overlap using the same logic as backend
      // If either program has no end date, check if the start dates conflict
      if (!newEnd || !existingEnd) {
        // If new program has no end date and starts before or on existing program's start
        if (!newEnd && newStart <= existingStart) {
          // Check if existing program ends after new program starts (or has no end)
          return !existingEnd || existingEnd >= newStart;
        }
        // If existing program has no end date and starts before or on new program's start
        if (!existingEnd && existingStart <= newStart) {
          // Check if new program ends after existing program starts (or has no end)
          return !newEnd || newEnd >= existingStart;
        }
      }

      // Both have end dates - check for standard overlap
      // Programs overlap if one starts before the other ends
      return (
        newStart <= (existingEnd || existingStart) &&
        existingStart <= (newEnd || newStart)
      );
    });

    if (overlappingProgram) {
      const existingStartStr = new Date(overlappingProgram.start_date)
        .toISOString()
        .split("T")[0];
      const existingEndStr = overlappingProgram.end_date
        ? new Date(overlappingProgram.end_date).toISOString().split("T")[0]
        : "ongoing";

      setErrorMessage(
        `Date range overlaps with existing ${overlappingProgram.program_type} program (Round ${overlappingProgram.round}: ${existingStartStr} to ${existingEndStr})`
      );
      return;
    }

    // If all validations pass, proceed with update mutation
    try {
      await updateTreatment({
        variables: {
          id: treatmentData.id,
          data: {
            start_date: newStart,
            end_date: newEnd,
            specialist_id: formData.specialist_id!,
          },
        },
      });

      // Close modal first
      onClose();
      reset();
      setStartDateValue(null);
      setEndDateValue(null);

      // Then show success message after a brief delay
      setTimeout(() => {
        setSuccessMessage("Treatment updated successfully!");
        onSuccess?.();
      }, 100);
    } catch (err) {
      setErrorMessage("Failed to update. Please try again.");
    }
  };

  const handleClose = () => {
    onClose();
    reset();
    setErrorMessage(undefined);
    setSuccessMessage(undefined);
    setStartDateValue(null);
    setEndDateValue(null);
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <UserModalContainer>
          <Box sx={ModalStyle}>
            <Grid container justifyContent="space-between">
              <Typography variant="h6">Edit Treatment Record</Typography>
              <Button
                variant="outlined"
                onClick={handleClose}
                className="button"
              >
                X
              </Button>
            </Grid>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <Title>Program Type</Title>
                    <Text>{treatmentData?.programType}</Text>
                  </Stack>
                </Grid>

                <Grid item xs={6}>
                  {/* <Stack spacing={1}>
                    <label>Round Number</label>
                    <Controller
                      name="roundNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          disabled
                          InputProps={{
                            readOnly: true,
                          }}
                          value={field.value}
                        />
                      )}
                    />
                    {errors.roundNumber && (
                      <Typography color="error" variant="caption">
                        {errors.roundNumber.message}
                      </Typography>
                    )}
                  </Stack> */}
                  <Stack spacing={1}>
                    <Title>Round Number</Title>
                    <Text>{treatmentData?.roundNumber}</Text>
                  </Stack>
                </Grid>

                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <Title>Therapist*</Title>
                    <Controller
                      name="specialist_id"
                      control={control}
                      rules={{ required: "Select a therapist" }}
                      render={({ field }) => (
                        <MuiSelect {...field} displayEmpty>
                          <MenuItem value="" disabled>
                            Select a therapist
                          </MenuItem>
                          {specialistData.map((s) => (
                            <MenuItem key={s.value} value={s.value}>
                              {s.firstName}
                            </MenuItem>
                          ))}
                        </MuiSelect>
                      )}
                    />
                    {errors.specialist_id && (
                      <Typography color="error" variant="caption">
                        {errors.specialist_id.message}
                      </Typography>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <Title>Start Date*</Title>
                    <Controller
                      name="start_date"
                      control={control}
                      rules={{ required: "Start date is required" }}
                      // TODO: Fix this the next time the file is edited.
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      render={({ field: { ref, ...rest } }) => (
                        <DateTimePickerComponent
                          dateValue={startDateValue}
                          handleChange={(d) => {
                            setStartDateValue(d);
                            rest.onChange(d);
                          }}
                          label="Start Date"
                          required
                        />
                      )}
                    />
                    {errors.start_date && (
                      <Typography color="error" variant="caption">
                        {errors.start_date.message}
                      </Typography>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <Title>End Date*</Title>
                    <Controller
                      name="end_date"
                      control={control}
                      rules={{
                        required: "End date is required",
                        validate: (val) => {
                          if (!val || !watchStart) return true;

                          // Check if end date is after start date
                          if (val <= watchStart) {
                            return "End date must be after start date";
                          }

                          // Check if dates are the same (comparing date part only using UTC)
                          const startDateOnly = new Date(watchStart);
                          startDateOnly.setUTCHours(0, 0, 0, 0);
                          const endDateOnly = new Date(val);
                          endDateOnly.setUTCHours(0, 0, 0, 0);

                          if (
                            startDateOnly.getTime() === endDateOnly.getTime()
                          ) {
                            return "End date cannot be the same as start date";
                          }

                          return true;
                        },
                      }}
                      // TODO: Fix this the next time the file is edited.
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      render={({ field: { ref, ...rest } }) => (
                        <DateTimePickerComponent
                          dateValue={endDateValue}
                          handleChange={(d) => {
                            setEndDateValue(d);
                            rest.onChange(d);
                          }}
                          label="End Date"
                          required
                        />
                      )}
                    />
                    {errors.end_date && (
                      <Typography color="error" variant="caption">
                        {errors.end_date.message}
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>

              <Grid container justifyContent="flex-end" spacing={2} mt={1}>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    className="button"
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    className="button"
                  >
                    {loading ? "Updating..." : "Update"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </UserModalContainer>
      </Modal>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(undefined)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(undefined)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{errorMessage}</Alert>
      </Snackbar>
    </>
  );
};
